import React, { useState } from 'react';
import { Layout, Button, Space, Menu, Drawer } from 'antd';
import { 
  UserOutlined, LogoutOutlined, DashboardOutlined, SettingOutlined, 
  ShopOutlined, TableOutlined, CalendarOutlined,
  BarChartOutlined, AppstoreOutlined, MenuOutlined, CloseOutlined,
  RobotOutlined, AuditOutlined, LineChartOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';

const { Header } = Layout;

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Temel menü öğeleri
  const baseMenuItems = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      group: 'main'
    },
    {
      key: '/data-entry',
      icon: <TableOutlined />,
      label: 'Veri Girişi',
      group: 'data'
    },
    {
      key: '/hourly-report',
      icon: <ClockCircleOutlined />,
      label: 'Saatlik Rapor',
      group: 'data'
    },
    {
      key: '/all-brands-metrics',
      icon: <AppstoreOutlined />,
      label: 'Tüm Markalar',
      group: 'data'
    },
    {
      key: '/metrics-summary',
      icon: <LineChartOutlined />,
      label: 'Metrik Özeti',
      group: 'data'
    }
  ];

  // Admin kullanıcıları için menü öğeleri
  const adminMenuItems = [
    {
      key: '/audits',
      icon: <AuditOutlined />,
      label: 'Audit',
      group: 'audit'
    },
    {
      key: '/audit-settings',
      icon: <RobotOutlined />,
      label: 'Audit Ayarları',
      group: 'audit'
    },
    {
      key: '/brands',
      icon: <ShopOutlined />,
      label: 'Marka Ayarları',
      group: 'settings'
    },
    {
      key: '/metrics',
      icon: <BarChartOutlined />,
      label: 'Metrik Ayarları',
      group: 'settings'
    },
    {
      key: '/monthly-metrics',
      icon: <CalendarOutlined />,
      label: 'Aylık Metrikler',
      group: 'settings'
    },
  ];

  // Desktop için dropdown menü yapısı
  const getDesktopMenuItems = () => {
    const items = [
      {
        key: '/',
        icon: <DashboardOutlined />,
        label: 'Dashboard'
      },
      {
        key: '/data-entry',
        icon: <TableOutlined />,
        label: 'Veri Girişi'
      },
      {
        key: '/hourly-report',
        icon: <ClockCircleOutlined />,
        label: 'Saatlik Rapor'
      },
      {
        key: '/all-brands-metrics',
        icon: <AppstoreOutlined />,
        label: 'Tüm Markalar'
      },
      {
        key: '/metrics-summary',
        icon: <LineChartOutlined />,
        label: 'Metrik Özeti'
      }
    ];

    // Admin menülerini ekle
    if (user?.role === 'admin') {
      items.push(
        {
          key: 'audit',
          label: 'Audit Ai ✨',
          icon: <RobotOutlined />,
          children: [
            {
              key: '/audits',
              icon: <AuditOutlined />,
              label: 'Audit'
            },
            {
              key: '/audit-settings',
              icon: <RobotOutlined />,
              label: 'Audit Ayarları'
            }
          ]
        },
        {
          key: 'settings',
          label: 'Ayarlar',
          icon: <SettingOutlined />,
          children: [
            {
              key: '/brands',
              icon: <ShopOutlined />,
              label: 'Marka Ayarları'
            },
            {
              key: '/metrics',
              icon: <BarChartOutlined />,
              label: 'Metrik Ayarları'
            },
            {
              key: '/monthly-metrics',
              icon: <CalendarOutlined />,
              label: 'Aylık Metrikler'
            },
          ]
        }
      );
    }

    return items;
  };

  // Mobil için flatten edilmiş menü öğeleri
  const menuItems = user?.role === 'admin' 
    ? [...baseMenuItems, ...adminMenuItems]
    : baseMenuItems;

  const handleMenuClick = (key) => {
    navigate(key);
    setMobileMenuOpen(false);
  };

  return (
    <>
      <Header className="bg-white border-b border-gray-200 px-4 flex items-center justify-between shadow-sm">
        {/* Logo */}
        <div className="flex items-center">
          <div className="text-xl font-bold text-blue-600 mr-6">
            📊 Cirotab
          </div>
        </div>

        {/* Desktop Menu - Sadece büyük ekranlarda görünür */}
        <div className="hidden lg:flex flex-1 justify-center">
          <Menu
            mode="horizontal"
            selectedKeys={[location.pathname]}
            items={getDesktopMenuItems()}
            onClick={({ key }) => {
              // Dropdown parent'ları için navigate etme
              if (!key.startsWith('/')) return;
              navigate(key);
            }}
            className="border-none bg-transparent flex-1 justify-center"
            style={{ lineHeight: '64px' }}
          />
        </div>

        {/* Desktop User Info - Sadece büyük ekranlarda görünür */}
        <div className="hidden lg:flex items-center">
          <Space className="text-gray-700">
            <div className="user-info-badge">
              <UserOutlined className="mr-2" />
              <span className="font-medium">{user?.username}</span>
            </div>
            <Button 
              type="text" 
              icon={<LogoutOutlined />} 
              onClick={logout}
              className="text-gray-600 hover:text-red-500 hover:bg-red-50"
            >
              Çıkış
            </Button>
          </Space>
        </div>

        {/* Mobile Menu Button - Sadece küçük ekranlarda görünür */}
        <div className="flex lg:hidden items-center space-x-2">
          <div className="mobile-user-badge">
            <UserOutlined className="mr-1" />
            <span className="font-medium max-w-20 truncate">{user?.username}</span>
          </div>
          <Button 
            type="text" 
            icon={<MenuOutlined />}
            onClick={() => setMobileMenuOpen(true)}
            className="mobile-nav-button"
          />
        </div>
      </Header>

      {/* Mobile Drawer Menu */}
      <Drawer
        title={
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center">
              <div className="text-lg font-bold text-blue-600">
                📊 Cirotab
              </div>
            </div>
            <Button 
              type="text" 
              icon={<CloseOutlined />}
              onClick={() => setMobileMenuOpen(false)}
              className="text-gray-500"
            />
          </div>
        }
        placement="left"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        width={280}
        bodyStyle={{ padding: 0 }}
        headerStyle={{ borderBottom: '1px solid #f0f0f0', padding: '16px 24px' }}
        closable={false}
      >
        <div className="flex flex-col h-full">
          {/* User Info Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 mb-2">
            <div className="flex items-center">
              <div className="bg-white bg-opacity-20 rounded-full p-2 mr-3">
                <UserOutlined className="text-lg" />
              </div>
              <div>
                <div className="font-semibold">{user?.username}</div>
                <div className="text-blue-100 text-sm">
                  {user?.role === 'admin' ? 'Yönetici' : 'Kullanıcı'}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Menu */}
          <div className="flex-1 overflow-y-auto">
            <nav className="p-2">
              {/* Ana Sayfa */}
              <div className="mb-4">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-4">
                  Ana Sayfa
                </div>
                {menuItems.filter(item => item.group === 'main').map((item) => (
                  <button
                    key={item.key}
                    onClick={() => handleMenuClick(item.key)}
                    className={`
                      w-full flex items-center px-4 py-3 mb-1 rounded-lg text-left transition-all duration-200
                      ${location.pathname === item.key 
                        ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600' 
                        : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">{item.icon}</span>
                    <span className="font-medium">{item.label}</span>
                  </button>
                ))}
              </div>

              {/* Veri & Analiz */}
              <div className="mb-4">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-4">
                  Veri & Analiz
                </div>
                {menuItems.filter(item => item.group === 'data').map((item) => (
                  <button
                    key={item.key}
                    onClick={() => handleMenuClick(item.key)}
                    className={`
                      w-full flex items-center px-4 py-3 mb-1 rounded-lg text-left transition-all duration-200
                      ${location.pathname === item.key 
                        ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600' 
                        : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">{item.icon}</span>
                    <span className="font-medium">{item.label}</span>
                  </button>
                ))}
              </div>

              {/* Audit (Admin only) */}
              {user?.role === 'admin' && (
                <div className="mb-4">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-4">
                    ✨ Audit Ai
                  </div>
                  {menuItems.filter(item => item.group === 'audit').map((item) => (
                    <button
                      key={item.key}
                      onClick={() => handleMenuClick(item.key)}
                      className={`
                        w-full flex items-center px-4 py-3 mb-1 rounded-lg text-left transition-all duration-200
                        ${location.pathname === item.key 
                          ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600' 
                          : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                        }
                      `}
                    >
                      <span className="mr-3 text-lg">{item.icon}</span>
                      <span className="font-medium">{item.label}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Ayarlar (Admin only) */}
              {user?.role === 'admin' && (
                <div className="mb-4">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-4">
                    Ayarlar
                  </div>
                  {menuItems.filter(item => item.group === 'settings').map((item) => (
                    <button
                      key={item.key}
                      onClick={() => handleMenuClick(item.key)}
                      className={`
                        w-full flex items-center px-4 py-3 mb-1 rounded-lg text-left transition-all duration-200
                        ${location.pathname === item.key 
                          ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600' 
                          : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                        }
                      `}
                    >
                      <span className="mr-3 text-lg">{item.icon}</span>
                      <span className="font-medium">{item.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </nav>
          </div>

          {/* Logout Button */}
          <div className="border-t border-gray-200 p-4">
            <Button 
              type="default"
              icon={<LogoutOutlined />}
              onClick={() => {
                logout();
                setMobileMenuOpen(false);
              }}
              block
              className="bg-red-50 text-red-600 border-red-200 hover:bg-red-100 hover:border-red-300 font-medium"
            >
              Çıkış Yap
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default Navbar; 